import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { subscriptionAPI } from '../../api';
import { Sidebar } from '../dashboard_component';

function JoinSubscription() {
  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const { token: paramToken } = useParams();

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  // If token is provided in URL
  React.useEffect(() => {
    if (paramToken) {
      setToken(paramToken);
    }
  }, [paramToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token.trim()) {
      toast.error('Please enter a token');
      return;
    }

    setIsLoading(true);
    try {
      const response = await subscriptionAPI.acceptToken(token);
      if (response.success || response.message) {
        const successMessage = response.message || 'Successfully joined subscription!';
        toast.success(successMessage);
        setTimeout(() => {
          navigate('/cbt_creator/dashboard');
        }, 2000);
      } else {
        const errorMessage = response.error || response.detail || 'Invalid or expired token';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error joining subscription:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.data?.detail || 
                          error.message || 
                          'Invalid or expired token';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Modern Header */}
          <div className="mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent mb-3">
              Join Subscription
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl">
              Enter an invitation token to join a shared subscription plan
            </p>
          </div>

          {/* Main Content - Centered Card */}
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="max-w-md w-full">
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <div className="text-center mb-6 md:mb-8">
                  <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <i className="fas fa-user-plus text-blue-600 text-2xl"></i>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Join Subscription</h1>
                  <p className="text-gray-600 text-sm md:text-base">
                    Enter the invitation token to join a shared subscription plan
                  </p>
                </div>
                
                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invitation Token
                    </label>
                    <input
                      type="text"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      placeholder="Enter your invitation token"
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      required
                    />
                    <p className="text-xs md:text-sm text-gray-500 mt-2">
                      You can get this token from the subscription owner
                    </p>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isLoading || !token.trim()}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3.5 rounded-lg font-semibold disabled:opacity-50 transition-all duration-300 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Joining...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="fas fa-user-check mr-2"></i>
                        Join Subscription
                      </span>
                    )}
                  </button>
                </form>
                
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">How to get a token?</h3>
                  <ul className="text-xs md:text-sm text-gray-600 space-y-1">
                    <li className="flex items-start">
                      <i className="fas fa-envelope text-blue-500 mt-1 mr-2 text-xs"></i>
                      Ask the subscription owner for an invitation token
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-share-alt text-green-500 mt-1 mr-2 text-xs"></i>
                      Or request an email invitation from them
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-clock text-amber-500 mt-1 mr-2 text-xs"></i>
                      Tokens expire after 7 days
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info Section */}
          <div className="mt-8 md:mt-12 max-w-2xl mx-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-blue-800 mb-3 flex items-center">
                <i className="fas fa-info-circle mr-2"></i>
                About Shared Subscriptions
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-blue-700 mb-2">Benefits:</h4>
                  <ul className="text-blue-600 text-sm space-y-1">
                    <li className="flex items-start">
                      <i className="fas fa-check-circle text-green-500 mt-0.5 mr-2"></i>
                      Access premium features
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-check-circle text-green-500 mt-0.5 mr-2"></i>
                      Cost-effective solution
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-check-circle text-green-500 mt-0.5 mr-2"></i>
                      Team collaboration enabled
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-700 mb-2">How it works:</h4>
                  <ul className="text-blue-600 text-sm space-y-1">
                    <li className="flex items-start">
                      <i className="fas fa-key text-amber-500 mt-0.5 mr-2"></i>
                      Get invitation token from owner
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-clock text-amber-500 mt-0.5 mr-2"></i>
                      Tokens expire in 7 days
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-users text-amber-500 mt-0.5 mr-2"></i>
                      One subscription, multiple users
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8">
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <a
                href="https://wa.me/2348113315252"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center gap-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3.5 px-4 sm:px-6 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg flex-1"
              >
                <i className="fab fa-whatsapp text-lg md:text-xl"></i>
                <span className="text-sm sm:text-base truncate">Chat on WhatsApp</span>
              </a>

              <button
                onClick={() => navigate('/cbt_creator/dashboard')}
                className="group flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3.5 px-4 sm:px-6 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg flex-1"
              >
                <i className="fas fa-arrow-left text-lg md:text-xl"></i>
                <span className="text-sm sm:text-base truncate">Back to Dashboard</span>
              </button>
            </div>
          </div>

          <ToastContainer />
        </div>
      </div>
    </div>
  );
}

export default JoinSubscription;